import { useCallback } from "react";
import { yupResolver } from "@hookform/resolvers/yup";

import Grid from "@mui/material/Grid";

import { useFormWithNotification } from "@covertree/hooks";
import { YesNo } from "@covertree/types";
import { FormConditionRadioGroup, FormTextField } from "@covertree/ui";

import { useGetClaimDetailsForLossType } from "@hooks/useGetClaimDetailsForLossType";
import { useAppDispatch } from "@redux/hooks";
import { StepPath } from "@routing/paths";
import { submitClaimDetails } from "@redux/slices";
import { LossType } from "@api/operations.generated";

import { InjuredPersonsSection } from "../shared/InjuredPersonsSection";
import { ClaimsForm } from "../../ClaimsForm";
import { FireOrSmokeDamageForm, validation } from "./form";

export function FireOrSmokeDamage() {
  const dispatch = useAppDispatch();

  const defaultValues = useGetClaimDetailsForLossType();

  const form = useFormWithNotification({
    mode: "all",
    reValidateMode: "onSubmit",
    defaultValues: { ...defaultValues },
    resolver: yupResolver(validation),
  });
  const { control, watch } = form;

  const fireResponse = watch("fireResponse");

  const submit = useCallback((formValues: FireOrSmokeDamageForm) => {
    dispatch(
      submitClaimDetails({
        value: formValues,
        type: LossType.FireOrSmokeDamage,
      })
    );
  }, []);

  return (
    <ClaimsForm
      form={form}
      onSubmit={submit}
      navigateToStep={StepPath.UPLOAD_CLAIM_DOCUMENTS}
      dataTestId="fire-or-smoke-damage">
      <Grid item xs={12}>
        <FormTextField
          id="claim-description"
          dataTestId="claim-description"
          name="claimDescription"
          multiline
          rows={4}
          label="How do you believe the fire started? How was it discovered?"
          control={control}
        />
      </Grid>
      <Grid item xs={12}>
        <FormConditionRadioGroup
          id="fire-response"
          name="fireResponse"
          label="Did the Fire Department respond to the fire?"
          control={control}
        />
      </Grid>
      {fireResponse === YesNo.Yes && (
        <>
          <Grid item xs={12}>
            <FormTextField
              id="fire-department"
              dataTestId="fire-department"
              name="fireDepartment"
              label="Name of fire department"
              control={control}
              optional
            />
          </Grid>

          <Grid item xs={12}>
            <FormTextField
              id="police-department"
              dataTestId="police-department"
              name="policeDepartment"
              label="Name of police department"
              control={control}
              optional
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <InjuredPersonsSection label="Was anyone injured in the fire?" />
      </Grid>
    </ClaimsForm>
  );
}
